import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { RevealHeading } from "@/components/Reveal";
import { Upload, X, Check, Loader2, Sparkles, Plus, Info } from "lucide-react";

const PANEL_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b08e7dc5-f5ef-47e4-ba74-cce130d25cfc/images/ae7b5b8e64de091fa65032c696590821246f387d029643db5935e6df4e46fcb9.jpeg";

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">
      {label} {required && <span className="text-neon">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-matte border border-[#222] px-4 py-3 font-mono text-sm text-[#FAFAFA] placeholder:text-[#555] outline-none focus:border-neon focus:ring-1 focus:ring-neon transition-colors rounded-none";

export default function BuildRequest() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "",
    swap_details: "", description: "",
  });
  const [engineType, setEngineType] = useState("stock"); // "stock" | "swapped"
  const [checklist, setChecklist] = useState([]); // [{label, checked}]
  const [generating, setGenerating] = useState(false);
  const [newItem, setNewItem] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generateChecklist = async () => {
    if (form.description.trim().length < 8) {
      toast.error("Describe the work you want done first.");
      return;
    }
    setGenerating(true);
    try {
      const vehicle = `${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model}`.trim();
      const engine = engineType === "swapped" ? `swapped — ${form.swap_details}` : "stock";
      const { data } = await api.post("/build/checklist", {
        description: form.description,
        vehicle,
        engine,
      });
      setChecklist((data.items || []).map((label) => ({ label, checked: true })));
      toast.success("Checklist generated — review & edit below");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate checklist");
    } finally {
      setGenerating(false);
    }
  };

  const toggleItem = (i) =>
    setChecklist((c) => c.map((it, idx) => (idx === i ? { ...it, checked: !it.checked } : it)));
  const editItem = (i, label) =>
    setChecklist((c) => c.map((it, idx) => (idx === i ? { ...it, label } : it)));
  const removeItem = (i) => setChecklist((c) => c.filter((_, idx) => idx !== i));
  const addItem = () => {
    const v = newItem.trim();
    if (!v) return;
    setChecklist((c) => [...c, { label: v, checked: true }]);
    setNewItem("");
  };

  const onUpload = async (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    setUploading(true);
    try {
      for (const file of chosen) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFile = (path) => setFiles((prev) => prev.filter((f) => f.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Please fill in name, email and description.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/requests", {
        type: "build",
        ...form,
        engine_type: engineType,
        swap_details: engineType === "swapped" ? form.swap_details : "",
        build_checklist: checklist,
        attachments: files.map((f) => f.path),
      });
      setDone(true);
      toast.success("Build request submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-void min-h-screen" data-testid="build-request-page">
      <Navbar />

      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-16 md:pt-20">
        {/* Left visual */}
        <div className="relative border-b lg:border-b-0 lg:border-r border-[#222] overflow-hidden min-h-[40vh] lg:min-h-0">
          <img src={PANEL_IMG} alt="Performance Build" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-void/60" />
          <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-14">
            <div className="text-xs uppercase tracking-[0.3em] text-neon mb-5">// Full Build & Engine Swaps</div>
            <h1 className="font-display uppercase text-6xl md:text-7xl lg:text-8xl leading-[0.82] tracking-tight">
              <RevealHeading lines={["Performance", "Build"]} />
            </h1>
            <p className="mt-6 font-mono text-sm text-[#9CA3AF] max-w-sm">
              Built motors, engine swaps and complete performance packages. Describe the vision — we turn it into a plan.
            </p>
          </div>
        </div>

        {/* Right form */}
        <div className="p-6 md:p-14 flex items-center">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full text-center py-20"
              data-testid="request-success"
            >
              <div className="w-16 h-16 bg-neon flex items-center justify-center mx-auto mb-8">
                <Check size={32} className="text-void" />
              </div>
              <h2 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Build Request Received</h2>
              <p className="mt-4 font-mono text-sm text-[#9CA3AF] max-w-md mx-auto">
                Thanks — your build request is in. Our team will reach out to {form.email} shortly.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="w-full max-w-xl mx-auto space-y-6" data-testid="build-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <input data-testid="field-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="John Doe" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="field-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </Field>
              </div>
              <Field label="Phone">
                <input data-testid="field-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Make">
                  <input data-testid="field-make" className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Nissan" />
                </Field>
                <Field label="Model">
                  <input data-testid="field-model" className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="GT-R" />
                </Field>
                <Field label="Year">
                  <input data-testid="field-year" className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2018" />
                </Field>
              </div>

              {/* Engine selection */}
              <div>
                <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Engine</span>
                <div className="grid grid-cols-2 gap-2">
                  {["stock", "swapped"].map((opt) => (
                    <button
                      type="button"
                      key={opt}
                      data-testid={`engine-${opt}`}
                      onClick={() => setEngineType(opt)}
                      className={`border px-4 py-3 text-left uppercase tracking-[0.15em] text-xs font-bold transition-colors ${
                        engineType === opt ? "border-neon bg-neon/5 text-neon" : "border-[#222] text-[#9CA3AF] hover:border-[#444]"
                      }`}
                    >
                      {opt === "stock" ? "Stock Engine" : "Engine Swap"}
                    </button>
                  ))}
                </div>
                {engineType === "swapped" && (
                  <div className="mt-3">
                    <input
                      data-testid="field-swap-details"
                      className={inputCls}
                      value={form.swap_details}
                      onChange={set("swap_details")}
                      placeholder="Target swap engine + notes (e.g. LS3 V8, 2JZ-GTE, K24 turbo)"
                    />
                  </div>
                )}
              </div>

              {/* Work description */}
              <Field label="Desired Work / Build Vision" required>
                <textarea
                  data-testid="field-description"
                  rows={5}
                  className={inputCls}
                  value={form.description}
                  onChange={set("description")}
                  placeholder="Describe everything you want done — power goals, parts, engine work, fabrication, tuning…"
                />
              </Field>

              <button
                type="button"
                data-testid="generate-checklist-btn"
                onClick={generateChecklist}
                disabled={generating}
                className="w-full border border-neon text-neon font-bold uppercase tracking-[0.2em] text-xs py-3.5 flex items-center justify-center gap-2 hover:bg-neon hover:text-void transition-colors disabled:opacity-50"
              >
                {generating ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><Sparkles size={15} /> Generate Work Checklist</>}
              </button>

              {/* Generated checklist */}
              {checklist.length > 0 && (
                <div data-testid="build-checklist" className="border border-[#222] bg-matte">
                  <div className="px-5 py-4 border-b border-[#222] flex items-center justify-between">
                    <span className="font-display uppercase text-xl tracking-tight">Work <span className="text-neon">Checklist</span></span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Review & edit</span>
                  </div>
                  <div className="p-5 space-y-2">
                    {checklist.map((it, i) => (
                      <div key={i} data-testid={`checklist-item-${i}`} className="flex items-center gap-3 border border-[#222] px-3 py-2.5 bg-void">
                        <button
                          type="button"
                          data-testid={`checklist-toggle-${i}`}
                          onClick={() => toggleItem(i)}
                          className={`w-4 h-4 border flex items-center justify-center shrink-0 ${it.checked ? "border-neon bg-neon" : "border-[#555]"}`}
                        >
                          {it.checked && <Check size={12} className="text-void" />}
                        </button>
                        <input
                          data-testid={`checklist-input-${i}`}
                          value={it.label}
                          onChange={(e) => editItem(i, e.target.value)}
                          className="flex-1 min-w-0 bg-transparent font-mono text-sm text-[#FAFAFA] outline-none"
                        />
                        <button
                          type="button"
                          data-testid={`checklist-remove-${i}`}
                          onClick={() => removeItem(i)}
                          className="text-[#9CA3AF] hover:text-neon shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {/* Add item */}
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        data-testid="checklist-new-input"
                        value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                        placeholder="Add a work item…"
                        className={inputCls}
                      />
                      <button
                        type="button"
                        data-testid="checklist-add-btn"
                        onClick={addItem}
                        className="border border-[#333] text-neon px-4 py-3 hover:border-neon transition-colors shrink-0"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="flex items-start gap-2.5 border border-[#222] bg-void px-4 py-3 mt-2">
                      <Info size={15} className="text-neon mt-0.5 shrink-0" />
                      <p className="font-mono text-[11px] leading-relaxed text-[#9CA3AF]">
                        This checklist is auto-generated from your description as a starting scope — edit it freely. Final scope & pricing are confirmed after we review your request.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Uploads */}
              <div>
                <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-2">Reference Photos</span>
                <label
                  data-testid="upload-label"
                  className="flex items-center gap-3 border border-dashed border-[#333] px-4 py-4 cursor-pointer hover:border-neon transition-colors"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin text-neon" /> : <Upload size={18} className="text-neon" />}
                  <span className="font-mono text-sm text-[#9CA3AF]">{uploading ? "Uploading…" : "Click to upload photos"}</span>
                  <input data-testid="field-files" type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
                </label>
                {files.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {files.map((f) => (
                      <span key={f.path} className="flex items-center gap-2 bg-matte border border-[#222] px-3 py-2 text-xs font-mono text-[#FAFAFA]">
                        {f.filename}
                        <button type="button" onClick={() => removeFile(f.path)} data-testid={`remove-file-${f.path}`} className="text-[#9CA3AF] hover:text-neon">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                data-testid="submit-request-btn"
                disabled={submitting}
                className="w-full bg-neon text-void font-bold uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 hover:bg-[#FAFAFA] transition-colors disabled:opacity-50"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : "Submit Build Request"}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
